import React, {
  useReducer,
  useEffect,
  useCallback,
  useRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { getWidthForElement, throttle } from '../../services/utilityFunctions';
import Request from '../../services/request';
import Notifications from '../../services/notifications';
import Loader from '../../services/loader';
import { useSwipeable } from '../../services/customHooks';

const PAGE_SIZE = 30;

const dataFetch = (url, error, page, headers = {}, query = {}) =>
  new Promise(resolve => {
    new Request()
      .get(
        url,
        { pagination: { page, perpage: PAGE_SIZE }, query },
        true,
        false,
        headers,
      )
      .done(resolve)
      .fail(err => {
        Notifications.showErrorFromRequest(err, error);
        resolve(null);
      });
  });

const ACTION = {
  SCROLL: 'scroll',
  RESIZE: 'resize',
  FETCH: 'fetch',
  RESET: 'reset',
};

const SCROLL = {
  INCREMENT: 'increment',
  DECREMENT: 'decrement',
  ABSOLUTE: 'absolute',
};

const FETCH = {
  INITIATE: 'initiate',
  CONCLUDE: 'conclude',
};

const initialState = items => ({
  buttonState: {
    showPrevButton: false,
    showNextButton: false,
  },
  sizes: {
    itemSize: 0,
    containerSize: 0,
  },
  itemsScrolled: 0,
  data: { items: items || [], totalItems: null },
  isReady: false,
  isLoading: false,
});

const shouldShowButtons = (sizes, itemsScrolled, totalItems) => {
  const itemsPerContainer =
    Math.floor(sizes.containerSize / sizes.itemSize) > 1
      ? Math.floor(sizes.containerSize / sizes.itemSize)
      : 1;
  return {
    showPrevButton: itemsScrolled > 0,
    showNextButton:
      totalItems > 1 ? totalItems - itemsScrolled > itemsPerContainer : false,
  };
};

const scrollReducer = (state, action) => {
  let itemsScrolled;
  switch (action.payload) {
    case SCROLL.INCREMENT:
      itemsScrolled = state.itemsScrolled + 1;
      break;
    case SCROLL.DECREMENT:
      itemsScrolled = state.itemsScrolled - 1;
      break;
    default: {
      if (typeof action.payload === 'number') {
        itemsScrolled = action.payload;
      } else {
        throw new Error('Unexpected Action');
      }
    }
  }
  const buttonState = shouldShowButtons(
    state.sizes,
    itemsScrolled,
    state.data.totalItems,
  );
  return { itemsScrolled, buttonState };
};

const resizeReducer = (state, action) => ({
  buttonState: shouldShowButtons(
    action.payload,
    state.itemsScrolled,
    state.data.totalItems,
  ),
  sizes: action.payload,
});

const fetchReducer = (state, action) => {
  const { fetchType, data } = action.payload;
  const onlyMeta = state.data.items.length > 0 && !state.data.totalItems;

  switch (fetchType) {
    case FETCH.INITIATE:
      return { isLoading: true };
    case FETCH.CONCLUDE:
      return {
        isLoading: false,
        isReady: true,
        buttonState: shouldShowButtons(
          state.sizes,
          state.itemsScrolled,
          data.total,
        ),
        data: {
          items: onlyMeta
            ? state.data.items
            : state.data.items.concat(data.items),
          totalItems: data.total,
        },
      };
    default:
      throw new Error('Unexpected Action');
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION.SCROLL:
      return { ...state, ...scrollReducer(state, action) };
    case ACTION.RESIZE:
      return { ...state, ...resizeReducer(state, action) };
    case ACTION.FETCH:
      return { ...state, ...fetchReducer(state, action) };
    case ACTION.RESET:
      return initialState();
    default:
      throw new Error('Unexpected Action');
  }
};

const ServerSideCarousel = React.forwardRef((props, ref) => {
  const {
    id,
    children,
    items: initialItems,
    fetchUrl,
    defaultFetchError,
    placeholder,
    fetchHeaders,
    fetchQuery,
  } = props;

  const outerContainer = useRef(null);
  const innerContainer = useRef(null);
  const loader = useRef(id ? new Loader(`#${id}`) : null);

  const [state, dispatch] = useReducer(reducer, initialItems, initialState);
  const {
    buttonState: { showPrevButton, showNextButton },
    sizes: { itemSize },
    itemsScrolled,
    data: { items, totalItems },
    isReady,
    isLoading,
  } = state;

  const handleWindowResize = useCallback(
    throttle(() => {
      let newItemSize = 0;
      let newContainerSize = 0;
      if (outerContainer.current) {
        newContainerSize = getWidthForElement(outerContainer.current);
      }
      if (innerContainer.current && innerContainer.current.firstChild) {
        newItemSize = getWidthForElement(innerContainer.current.firstChild);
      }
      dispatch({
        type: ACTION.RESIZE,
        payload: { itemSize: newItemSize, containerSize: newContainerSize },
      });
    }, 100),
    [],
  );

  useEffect(() => {
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, [children]);

  const performRequest = useCallback(
    page => {
      dispatch({ type: ACTION.FETCH, payload: { fetchType: FETCH.INITIATE } });
      dataFetch(
        fetchUrl,
        defaultFetchError,
        page,
        fetchHeaders,
        fetchQuery,
      ).then(result => {
        const data = result
          ? { items: result.data, total: result.meta.total }
          : { items: [], total: 0 };
        dispatch({
          type: ACTION.FETCH,
          payload: { fetchType: FETCH.CONCLUDE, data },
        });
        handleWindowResize();
      });
    },
    [dispatch, fetchUrl, defaultFetchError],
  );

  useEffect(() => {
    if (!isReady) performRequest(1);
  }, [isReady]);

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) {
      const leftToScroll = items.length - itemsScrolled;
      const hasMore = items.length < totalItems;
      if (hasMore && leftToScroll < PAGE_SIZE / 2) {
        const nextPage = Math.ceil(items.length / PAGE_SIZE) + 1;
        performRequest(nextPage);
      }
    }
  }, [itemsScrolled, items, totalItems, isLoading]);

  const scrollForward = useCallback(() => {
    if (isReady) {
      dispatch({ type: ACTION.SCROLL, payload: SCROLL.INCREMENT });
    }
  }, [isReady, dispatch]);

  const scrollBack = useCallback(() => {
    if (isReady) {
      dispatch({ type: ACTION.SCROLL, payload: SCROLL.DECREMENT });
    }
  }, [isReady, dispatch]);

  const handleWheel = useCallback(
    throttle(deltaX => {
      if (deltaX > 0 && showNextButton) {
        scrollForward();
      } else if (deltaX < 0 && showPrevButton) {
        scrollBack();
      }
    }, 200),
    [showNextButton, showPrevButton],
  );

  useEffect(() => dispatch({ type: ACTION.RESET }), [fetchUrl]);
  useImperativeHandle(ref, () => ({
    reset: () => dispatch({ type: ACTION.RESET }),
    scrollToIndex: v => dispatch({ type: ACTION.SCROLL, payload: v }),
  }));

  const onSwipeRef = useSwipeable({
    onSwipedLeft: scrollForward,
    onSwipedRight: scrollBack,
  });
  const handleOuterRef = el => {
    if (el) {
      outerContainer.current = el;
      onSwipeRef(el);
    }
  };

  const showPlaceholder = items.length < 1 && placeholder;

  return (
    <div
      id={id}
      ref={handleOuterRef}
      className="carousel-container-wrapper"
      onWheel={({ deltaX }) => handleWheel(deltaX)}
    >
      <button
        ref={ref}
        style={{ display: 'none' }}
        type="button"
        onClick={() => dispatch({ type: ACTION.RESET })}
      >
        Reset
      </button>
      {showPrevButton && (
        <button
          className="carousel-scroll prev"
          type="button"
          onClick={scrollBack}
        >
          <i className="fa fa-angle-left" />
        </button>
      )}
      <div
        className="carousel-container"
        ref={innerContainer}
        style={{
          transform: `translateX(-${itemSize * itemsScrolled}px)`,
        }}
      >
        {showPlaceholder ? placeholder : children(items)}
      </div>
      {showNextButton && (
        <button
          className="carousel-scroll next"
          type="button"
          onClick={scrollForward}
        >
          <i className="fa fa-angle-right" />
        </button>
      )}
    </div>
  );
});

ServerSideCarousel.propTypes = {
  id: PropTypes.string,
  children: PropTypes.func.isRequired,
  fetchUrl: PropTypes.string.isRequired,
  defaultFetchError: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  placeholder: PropTypes.element,
  fetchHeaders: PropTypes.shape({}),
  fetchQuery: PropTypes.shape({}),
};

ServerSideCarousel.defaultProps = {
  id: null,
  items: null,
  defaultFetchError: '',
  placeholder: null,
  fetchHeaders: {},
  fetchQuery: {},
};

export default ServerSideCarousel;
